// apps/admin/src/features/billing/InvoicePaymentHistory.tsx
//
// Phase 8.2 Wave 3 Task 12 — Invoice Detail A2 payment history sidebar.
//
// vg-binding: CONTEXT:decisions=P8.D-485,P8.D-493,P8.D-497
// vg-binding: PHASE:08.2-admin-finance-catalog
//
//  A2: payment history list in the right rail of Invoice
// Detail.: each payment row exposes a "Reverse Payment"
// trigger when the current user holds `invoice:payment_reverse` and the
// payment is in APPROVED state. Reversed payments render with
// strikethrough + REVERSED badge for at-a-glance audit visibility.
//
// This is a presentational shell — the modal that captures the
// 50-500-char reason and POSTs to
// `/api/v1/admin/invoices/:id/payments/:paymentId/reverse` is owned by
// Wave 3 Task 15 (`ReversePaymentModal`). This component exposes the
// `onReverseClick(payment)` callback so the parent page can wire either
// the task-15 modal or a stub fallback while task-15 is in flight.

import type { JSX } from 'react';
import { Button } from '@printway/ui';
import { Money } from '../../components/ui/Money.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import { PERMISSIONS } from '../../lib/permissions.js';
import { useAuthStore } from '../../stores/useAuthStore.js';

// Payment statuses with a localized label under `invoice.paymentStatusLabels.*`;
// any value outside this set falls back to the raw enum string.
const KNOWN_PAYMENT_STATUSES = new Set<string>([
  'PENDING_APPROVAL',
  'APPROVED',
  'REJECTED',
  'REVERSED',
]);

// ---------------------------------------------------------------------------
// Shared wire types — mirror of P4.1 API-CONTRACTS InvoicePaymentRecord.
// Kept loose (string-union for status) so this component compiles even
// when the parent page narrows to a richer local type.
// ---------------------------------------------------------------------------

export type InvoicePaymentStatus =
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'REJECTED'
  | 'REVERSED';

export interface InvoicePaymentHistoryRow {
  id: string;
  amount_usd_cents: number;
  gateway: string;
  reference: string;
  status: InvoicePaymentStatus | string;
  notes?: string | null;
  proof_file_url?: string | null;
  submitted_by?: string | null;
  submitted_at?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  rejected_by?: string | null;
  rejected_at?: string | null;
  reject_reason?: string | null;
  reversed_at?: string | null;
  reverse_reason?: string | null;
  created_at?: string | null;
}

export interface InvoicePaymentHistoryProps {
  payments: InvoicePaymentHistoryRow[];
  /** Triggers task-13 Mark Paid modal at the page level (header CTA fallback). */
  loading?: boolean;
  /** Per-row reverse trigger — owned by task-15 modal in the parent page. */
  onReverseClick?: (payment: InvoicePaymentHistoryRow) => void;
  /**
   * Per-row review trigger for PENDING_APPROVAL payments — opens the
   * existing AdminApprovePaymentModal owned by the parent page.
   */
  onReviewClick?: (payment: InvoicePaymentHistoryRow) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 10);
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'APPROVED':
      return 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800';
    case 'PENDING_APPROVAL':
      return 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800';
    case 'REJECTED':
      return 'bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800';
    case 'REVERSED':
      return 'bg-pw-surface text-pw-text border border-pw-border';
    default:
      return 'bg-pw-surface text-pw-text border border-pw-border';
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Right-rail Payment History block for Invoice Detail A2. Iterates
 * `payments[]` from the invoice detail response, renders amount via
 * `<Money>`, and exposes per-row Review (PENDING) + Reverse
 * (APPROVED, admin-only `invoice:payment_reverse`).
 *
 * Reversed payments are rendered strikethrough + with a REVERSED pill so
 * audit reviewers can spot inversions without expanding the row.
 */
export function InvoicePaymentHistory(
  props: InvoicePaymentHistoryProps,
): JSX.Element {
  const { payments, loading, onReverseClick, onReviewClick } = props;
  const { t } = useTranslation('finance');
  const canReverse = useAuthStore((s) =>
    s.hasPermission(PERMISSIONS.INVOICE_PAYMENT_REVERSE),
  );

  if (loading) {
    return (
      <p
        className="mt-3 text-sm text-pw-text-muted"
        data-testid="invoice-payment-history-loading"
      >
        {t('invoice.paymentHistory.loading')}
      </p>
    );
  }

  if (payments.length === 0) {
    return (
      <p
        className="mt-3 text-sm text-pw-text-muted"
        data-testid="invoice-payment-history-empty"
      >
        {t('invoice.paymentHistory.empty')}
      </p>
    );
  }

  return (
    <ul
      className="mt-3 divide-y divide-pw-border"
      data-testid="invoice-payment-history-list"
    >
      {payments.map((p) => {
        const isReversed = p.status === 'REVERSED' || Boolean(p.reversed_at);
        const isApproved = p.status === 'APPROVED' && !isReversed;
        const isPending = p.status === 'PENDING_APPROVAL';
        const rowClass = [
          'py-3 text-sm',
          isReversed ? 'opacity-75' : '',
        ]
          .filter(Boolean)
          .join(' ');
        const amountClass = [
          'font-medium',
          isReversed ? 'text-pw-text-muted line-through' : 'text-pw-text',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <li
            key={p.id}
            className={rowClass}
            data-testid={`invoice-payment-history-row-${p.id}`}
            data-payment-status={p.status}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={amountClass}>
                    <Money usdCents={p.amount_usd_cents} />
                  </span>
                  <span className="text-xs uppercase tracking-wide text-pw-text-muted">
                    {p.gateway}
                  </span>
                  {isReversed ? (
                    <span
                      className="inline-flex items-center rounded-md border border-pw-border bg-pw-surface px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-pw-text"
                      data-testid={`invoice-payment-reversed-badge-${p.id}`}
                    >
                      {t('invoice.paymentHistory.reversedBadge')}
                    </span>
                  ) : (
                    <span
                      className={[
                        'inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                        statusBadgeClass(p.status),
                      ].join(' ')}
                    >
                      {KNOWN_PAYMENT_STATUSES.has(p.status)
                        ? t(`invoice.paymentStatusLabels.${p.status}`)
                        : p.status}
                    </span>
                  )}
                </div>
                <p className="mt-1 truncate text-xs text-pw-text-muted">
                  {t('invoice.paymentHistory.refPrefix')}{' '}
                  <span className="font-mono">{p.reference}</span>
                </p>
                <p className="text-xs text-pw-text-muted">
                  {t('invoice.paymentHistory.submittedPrefix')}{' '}
                  {formatDate(p.submitted_at ?? p.created_at)}
                  {p.approved_at
                    ? ` · ${t('invoice.paymentHistory.approvedPrefix')} ${formatDate(p.approved_at)}`
                    : ''}
                  {p.reversed_at
                    ? ` · ${t('invoice.paymentHistory.reversedPrefix')} ${formatDate(p.reversed_at)}`
                    : ''}
                </p>
                {p.reject_reason ? (
                  <p className="mt-1 rounded-md bg-red-50 dark:bg-red-950/40 px-2 py-1 text-xs text-red-700 dark:text-red-300">
                    {t('invoice.paymentHistory.rejectedPrefix')} {p.reject_reason}
                  </p>
                ) : null}
                {p.reverse_reason ? (
                  <p className="mt-1 rounded-md bg-pw-surface px-2 py-1 text-xs text-pw-text">
                    {t('invoice.paymentHistory.reverseReasonPrefix')} {p.reverse_reason}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col items-end gap-1">
                {isPending && onReviewClick ? (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => {
                      onReviewClick(p);
                    }}
                    data-testid={`invoice-payment-review-btn-${p.id}`}
                  >
                    {t('invoice.paymentHistory.review')}
                  </Button>
                ) : null}
                {isApproved && canReverse && onReverseClick ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      onReverseClick(p);
                    }}
                    data-testid={`invoice-payment-reverse-btn-${p.id}`}
                  >
                    {t('invoice.paymentHistory.reverse')}
                  </Button>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default InvoicePaymentHistory;
